export type BlockerGroupKey = 'availability' | 'consideration' | 'followup' | 'experience';

export interface BlockerGroupDefinition {
  key: BlockerGroupKey;
  label: string;
  description: string;
  color: string;
  codes: readonly string[];
}

export const BLOCKER_GROUPS: readonly BlockerGroupDefinition[] = [
  {
    key: 'availability',
    label: 'Hàng hóa / danh mục',
    description: 'Hết hàng · không kinh doanh · thiếu demo',
    color: '#dc2626',
    codes: ['out_of_stock', 'not_in_business', 'demo_unavailable'],
  },
  {
    key: 'consideration',
    label: 'Giá & cân nhắc',
    description: 'Giá/ngân sách · so sánh · chỉ tham quan',
    color: '#d97706',
    codes: ['price_budget', 'comparing_shops', 'just_browsing'],
  },
  {
    key: 'followup',
    label: 'Chờ xử lý / liên hệ',
    description: 'Chờ phản hồi · chờ duyệt · thiếu liên hệ',
    color: '#2563eb',
    codes: ['waiting_response', 'internal_approval', 'missing_contact'],
  },
  {
    key: 'experience',
    label: 'Trải nghiệm / kỹ thuật',
    description: 'Vướng kỹ thuật · khách rời sớm',
    color: '#7c3aed',
    codes: ['technical_issue', 'customer_left_early'],
  },
] as const;

const BLOCKER_GROUP_BY_CODE = new Map<string, BlockerGroupKey>(
  BLOCKER_GROUPS.flatMap(group => group.codes.map(code => [code, group.key] as const)),
);

export interface BlockerTrendInput {
  period: string;
  blocker?: string | null;
  count?: number;
}

export interface BlockerGroupCounts extends Record<BlockerGroupKey, number> {}

export interface BlockerTrendPoint {
  period: string;
  known: number;
  unknown: number;
  counts: BlockerGroupCounts;
}

export interface BlockerSummary {
  known: number;
  unknown: number;
  counts: BlockerGroupCounts;
}

function emptyCounts(): BlockerGroupCounts {
  return { availability: 0, consideration: 0, followup: 0, experience: 0 };
}

function blockerGroup(code: string | null | undefined): BlockerGroupKey | null {
  return code ? BLOCKER_GROUP_BY_CODE.get(code) || null : null;
}

export function summarizeBlockers(rows: readonly BlockerTrendInput[]): BlockerSummary {
  const summary: BlockerSummary = { known: 0, unknown: 0, counts: emptyCounts() };
  rows.forEach(row => {
    const code = row.blocker || '';
    const count = Number(row.count ?? 1);
    if (!code || code === 'no_blocker' || !Number.isFinite(count) || count <= 0) return;
    const group = blockerGroup(code);
    if (!group) {
      summary.unknown += count;
      return;
    }
    summary.known += count;
    summary.counts[group] += count;
  });
  return summary;
}

export function buildBlockerTrend(
  rows: readonly BlockerTrendInput[],
  bucketPeriod: (period: string) => string,
): BlockerTrendPoint[] {
  const points = new Map<string, BlockerTrendPoint>();
  rows.forEach(row => {
    const period = bucketPeriod(row.period);
    const code = row.blocker || '';
    const count = Number(row.count ?? 1);
    if (!period || !code || code === 'no_blocker' || !Number.isFinite(count) || count <= 0) return;
    const point = points.get(period) || { period, known: 0, unknown: 0, counts: emptyCounts() };
    const group = blockerGroup(code);
    if (!group) point.unknown += count;
    else {
      point.known += count;
      point.counts[group] += count;
    }
    points.set(period, point);
  });
  return [...points.values()].sort((a, b) => a.period.localeCompare(b.period));
}
